import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";

function Compare({ before, after, title }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    const up = () => (dragging.current = false);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, []);

  const onMove = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const p = Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100));
    setPos(p);
  };

  return (
    <div
      ref={ref}
      onMouseMove={(e) => dragging.current && onMove(e.clientX)}
      onTouchMove={(e) => onMove(e.touches[0].clientX)}
      className="relative aspect-[4/3] w-full overflow-hidden bg-[#0B1215] select-none cursor-ew-resize"
      data-testid={`before-after-${title}`}
    >
      <img src={after} alt="After" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img src={before} alt="Before" loading="lazy" decoding="async" className="w-full h-full object-cover" draggable={false} />
      </div>

      <div className="absolute top-4 left-4 bg-[#F8F9FA] text-[#0B1215] px-3 py-1.5 font-mono-label z-10">
        BEFORE
      </div>
      <div className="absolute top-4 right-4 bg-[#FF5A36] text-[#F8F9FA] px-3 py-1.5 font-mono-label z-10">
        AFTER
      </div>

      <div
        className="absolute top-0 bottom-0 w-[3px] bg-[#F8F9FA] z-20 pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div
          onMouseDown={() => (dragging.current = true)}
          onTouchStart={() => (dragging.current = true)}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 bg-[#F8F9FA] border-2 border-[#0B1215] flex items-center justify-center cursor-ew-resize pointer-events-auto shadow-lg"
        >
          <span className="text-[#0B1215] font-bold">⇆</span>
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfter({ items }) {
  return (
    <section id="work" className="bg-[#0B1215] text-[#F8F9FA] py-24 sm:py-32 grain relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="font-mono-label text-[#FF5A36] mb-4">· REAL PROPERTIES / REAL RESULTS</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-2xl">
              The drag tells the truth. <span className="italic font-light">Slide</span> to see.
            </h2>
          </div>
          <p className="text-[#F8F9FA]/60 max-w-sm leading-relaxed">
            Every property below was handled by The Property Curator — actual before/after images, not stock. Your place is next.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {items.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <Compare before={item.before_url} after={item.after_url} title={item.id} />
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-xl tracking-tight">{item.title}</h3>
                  {item.description && (
                    <p className="text-sm text-[#F8F9FA]/60 mt-1">{item.description}</p>
                  )}
                </div>
                <span className="font-mono-label text-[#F8F9FA]/40 shrink-0 mt-1">
                  0{i + 1} / 0{items.length}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-[#F8F9FA]/10 flex items-center justify-center gap-3 text-center">
          <span className="w-2 h-2 bg-[#FF5A36] animate-pulse" />
          <p className="font-mono-label text-[#F8F9FA]/60">
            More before / afters added regularly — follow the work as it lands
          </p>
        </div>
      </div>
    </section>
  );
}
